import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Hourglass } from 'lucide-react';
import clsx from 'clsx';
import type { VenueSearchParams } from '../../types';

/**
 * The Venues `paused` URL param. Three values, not two: 'false' is the default view, and 'all' is
 * the explicit "no filter" — without that sentinel the default would come straight back the moment
 * the user turned it off, and a saved view could never mean "show everything".
 */
export type HiddenFilterValue = NonNullable<VenueSearchParams['paused']>;

const FILTER_OPTIONS: { value: HiddenFilterValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'false', label: 'Not hidden' },
  { value: 'true', label: 'Hidden' },
];

const DROPDOWN_WIDTH = 180;

interface VenueHiddenMenuProps {
  value: HiddenFilterValue;
  onChange: (value: HiddenFilterValue) => void;
}

/**
 * The Venues toolbar's hourglass: show all venues, only the ones you may write to now (the
 * default), or only the paused ones. See docs/VENUE_PAUSE.md.
 */
export function VenueHiddenMenu({ value, onChange }: VenueHiddenMenuProps) {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isOpen = position !== null;

  const open = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const left = rect.left + DROPDOWN_WIDTH > window.innerWidth - 8
      ? Math.max(8, rect.right - DROPDOWN_WIDTH)
      : rect.left;
    setPosition({ top: rect.bottom + 4, left });
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!buttonRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setPosition(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => (isOpen ? setPosition(null) : open())}
        title="Paused venues"
        className={clsx(
          'p-2 rounded-lg border transition-colors',
          // 'false' is the default, so it is not worth highlighting; the other two are choices.
          isOpen || value !== 'false'
            ? 'bg-primary-900/30 text-primary-400 border-primary-700/50'
            : 'bg-dark-elevated text-dark-text-muted border-dark-border hover:text-dark-text-secondary hover:bg-dark-border'
        )}
      >
        <Hourglass className="w-4 h-4" />
      </button>
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-9999 bg-dark-elevated border border-dark-border rounded-lg shadow-xl min-w-[180px] flex flex-col overflow-hidden animate-scaleIn"
          style={{ top: position.top, left: position.left }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-1">
            {FILTER_OPTIONS.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    setPosition(null);
                  }}
                  className={clsx(
                    'w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors',
                    isSelected ? 'bg-primary-600/20 text-primary-300' : 'text-dark-text-secondary hover:bg-dark-surface'
                  )}
                >
                  <Check className={clsx('w-3.5 h-3.5 flex-shrink-0', !isSelected && 'invisible')} />
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
